import { DateSeparator } from 'stream-chat-react';
import type { DateSeparatorProps } from 'stream-chat-react';

/**
 * INTERFACE: DateSeparatorCustomProps
 * 
 * Extends Stream Chat's DateSeparatorProps with custom languageCode
 * 
 * PROPERTIES:
 * - Inherits all DateSeparatorProps (date, position, etc.)
 * - languageCode: Translation object for day names
 */
interface DateSeparatorCustomProps extends DateSeparatorProps {
    languageCode: any;
}

const DateSeparatorCustom = ({ date, languageCode }: DateSeparatorCustomProps) => {
    /**
     * FUNCTION: getDay
     * Translates day number to localized day name
     * PARAMETERS:
     * - day: Number from 0-6 (0 = Sunday, 1 = Monday, etc.)
     * RETURNS:
     * Translated day name string
     * FALLBACK:
     * If translation missing, uses English name
     */
    const getDay = (day: number) => {
        switch (day) {
            case 1:
                return languageCode['Monday'] || 'Monday'; 
            case 2:
                return languageCode['Tuesday'] || 'Tuesday';
            case 3:
                return languageCode['Wednesday'] || 'Wednesday';
            case 4:
                return languageCode['Thursday'] || 'Thursday';
            case 5:
                return languageCode['Friday'] || 'Friday';
            case 6:
                return languageCode['Saturday'] || 'Saturday';
            case 0:
                return languageCode['Sunday'] || 'Sunday';
            default:
                return "";
        }
    }

    /**
     * FUNCTION: formatDate
     * Formats date into readable string with localized day name
     * PARAMETERS:
     * - date: Date object to format
     * FORMAT:
     * "DayOfWeek, Day Month, Year"
     * COMPONENTS:
     * - day: Numeric day of month (1-31)
     * - dayOfWeek: Translated day name from getDay()
     * - monthName: Short month name from toLocaleString()
     * - year: Full year (2025)
     */
    function formatDate(date: Date) {
        const day = date.getDate();
        const dayOfWeek = getDay(date.getDay());
        const monthName = date.toLocaleString('default', { month: 'short' });
        const year = date.getFullYear();

        return `${dayOfWeek}, ${day} ${monthName}, ${year}`;
    }

    /** 
     * Renders Stream Chat's DateSeparator with custom formatting
     * PROPS:
     * - formatDate: Custom function to format the date
     * - date: Date object to display
     * - position: 'center' - Centers separator in message list
     * APPEARANCE:
     * Stream Chat styles the separator with horizontal line and centered text
     */
    return (
        <DateSeparator formatDate={formatDate} date={date} position='center' />
    );
}

export default DateSeparatorCustom;
